import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, Users, DollarSign, Clock, Shield } from 'lucide-react';

const InsuranceChallenge = () => {
  const challenges = [
    {
      icon: TrendingDown,
      title: "Escalating Chronic Disease Costs",
      problem: "Chronic conditions like diabetes and cardiovascular disease represent long-term, high-cost commitments.",
      data: "In the UAE alone, 1.5M people have diabetes; 0.9M have heart disease.",
      impact: "Every new diabetic patient could cost hundreds of thousands of dirhams over their lifetime.",
      example: "A client ignores pre-diabetes symptoms, develops complications, and becomes a long-term financial liability.",
      color: "#0077B6",
      bgColor: "#0077B6/10"
    },
    {
      icon: Shield,
      title: "Fraud, Waste & Abuse (FWA)",
      problem: "Millions lost to unnecessary procedures, overbilling, and fraudulent claims.",
      impact: "No real-time tools to flag cheaper alternatives or detect suspicious patterns.",
      example: "Paying for premium-brand drugs when generics are available — repeated thousands of times yearly.",
      color: "#03045E",
      bgColor: "#03045E/10"
    },
    {
      icon: Users,
      title: "Fierce Competition & Customer Churn",
      problem: "Customers switch providers for small price differences because value-added services are lacking.",
      example: "Losing a corporate client to a competitor offering just 5% lower premiums.",
      color: "#90E0EF",
      bgColor: "#90E0EF/20"
    }
  ];

  return (
    <section id="challenge" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-red-100 border border-red-200 rounded-full mb-6">
            <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-red-600 font-semibold text-sm">Why Change is Urgent</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            The Three Biggest Threats to Health Insurers Today
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The traditional reactive model — waiting for illness, then paying rising costs — is no longer sustainable. 
            We address the core challenges that drain profits and loyalty.
          </p>
        </motion.div>

        {/* Challenges Grid */}
        <div className="space-y-12">
          {challenges.map((challenge, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                
                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''} space-y-6`}>
                  <div className="flex items-center space-x-4">
                    <div 
                      className={`p-4 rounded-2xl`}
                      style={{ backgroundColor: challenge.bgColor }}
                    >
                      <challenge.icon 
                        className="w-8 h-8" 
                        style={{ color: challenge.color }}
                      />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                        Challenge {index + 1}
                      </span>
                      <h3 className="text-2xl md:text-3xl font-bold text-[#03045E]">
                        {challenge.title}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <h4 className="font-semibold text-[#03045E] mb-2 flex items-center">
                        <DollarSign className="w-4 h-4 mr-2 text-red-500" />
                        Problem
                      </h4>
                      <p className="text-gray-700">{challenge.problem}</p>
                    </div>

                    {challenge.data && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <h4 className="font-semibold text-[#03045E] mb-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
                          Data
                        </h4>
                        <p className="text-gray-700">{challenge.data}</p>
                      </div>
                    )}

                    {challenge.impact && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <h4 className="font-semibold text-[#03045E] mb-2 flex items-center">
                          <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                          Impact
                        </h4>
                        <p className="text-gray-700">{challenge.impact}</p>
                      </div>
                    )}

                    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border border-red-100">
                      <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Real Example
                      </h4>
                      <p className="text-red-700 italic">{challenge.example}</p>
                    </div>
                  </div>
                </div>

                {/* Visual */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="relative"
                  >
                    {/* Main visualization */}
                    <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-bold text-[#03045E]">Cost Impact Analysis</h4>
                          <div 
                            className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                            style={{ backgroundColor: challenge.color }}
                          >
                            High Risk
                          </div>
                        </div>

                        {/* Mock chart */}
                        <div className="space-y-3">
                          {[
                            { label: 'Current Year', value: 70, trend: 'up' },
                            { label: '5-Year Projection', value: 95, trend: 'up' },
                            { label: 'Prevention Opportunity', value: 40, trend: 'down' }
                          ].map((item, i) => (
                            <div key={i} className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">{item.label}</span>
                                <span className={`text-sm font-bold ${
                                  item.trend === 'up' ? 'text-red-500' : 'text-green-500'
                                }`}>
                                  {item.value}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <motion.div
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${item.value}%` }}
                                  transition={{ duration: 1, delay: 0.5 + i * 0.2 }}
                                  viewport={{ once: true }}
                                  className={`h-2 rounded-full ${
                                    item.trend === 'up' ? 'bg-red-400' : 'bg-green-400'
                                  }`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-500">+{(index + 1) * 25}%</div>
                            <div className="text-xs text-gray-500">Annual Increase</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-[#03045E]">AED {(index + 1) * 2.5}M</div>
                            <div className="text-xs text-gray-500">Potential Loss</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Floating alert */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                      viewport={{ once: true }}
                      className="absolute -top-4 -right-4 bg-red-500 text-white p-3 rounded-full shadow-lg"
                    >
                      <AlertTriangle className="w-5 h-5" />
                    </motion.div>
                  </motion.div>
                </div>
              </div>

              {/* Connecting line (not for last item) */}
              {index < challenges.length - 1 && (
                <motion.div
                  initial={{ scaleY: 0 }}
                  whileInView={{ scaleY: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                  className="flex justify-center mt-12"
                >
                  <div className="w-0.5 h-16 bg-gradient-to-b from-gray-300 to-transparent origin-top"></div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-[#03045E] text-white rounded-3xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              These challenges are costing you millions. But they don't have to.
            </h3>
            <p className="text-lg text-blue-100 mb-6">
              HIERSTO transforms these threats into competitive advantages.
            </p>
            <motion.button
              onClick={() => {
                const element = document.getElementById('solution');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-[#90E0EF] text-[#03045E] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#90E0EF]/90 transition-all duration-300 shadow-lg"
            >
              See How HIERSTO Solves This
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsuranceChallenge;
